import { useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
    setMobileOpen(false);
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-2 text-sm rounded-md transition-colors whitespace-nowrap ${
      isActive
        ? "text-accent-500 font-medium"
        : "text-foreground-600 hover:text-foreground-950 hover:bg-background-100"
    }`;

  return (
    <header className="sticky top-0 z-50 w-full bg-background-50/90 backdrop-blur-md border-b border-background-200/60">
      <div className="w-full px-4 md:px-6">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0 group">
            <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-500 text-foreground-950">
              <i className="ri-film-line text-xl" />
            </span>
            <span className="font-heading text-lg font-bold text-foreground-950 whitespace-nowrap">
              三级片电影
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/" className={navLinkClass} end>
              首页
            </NavLink>
            <div
              className="relative"
              onMouseEnter={() => setCategoryOpen(true)}
              onMouseLeave={() => setCategoryOpen(false)}
            >
              <button
                type="button"
                onClick={() => setCategoryOpen((prev) => !prev)}
                className="flex items-center gap-1 px-3 py-2 text-sm rounded-md text-foreground-600 hover:text-foreground-950 hover:bg-background-100 transition-colors"
              >
                分类
                <i className={`ri-arrow-down-s-line transition-transform ${categoryOpen ? "rotate-180" : ""}`} />
              </button>
              {categoryOpen && (
                <div className="absolute left-0 top-full pt-2 w-64">
                  <div className="rounded-lg border border-background-200/70 bg-background-100 p-2 fade-in">
                    {categories.map((category) => (
                      <Link
                        key={category.id}
                        to={`/category/${category.id}`}
                        className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-foreground-600 hover:text-foreground-950 hover:bg-background-200/60 transition-colors"
                        onClick={() => setCategoryOpen(false)}
                      >
                        <span className="w-7 h-7 flex items-center justify-center rounded-md bg-primary-500/15 text-primary-300">
                          <i className={`${category.icon} text-base`} />
                        </span>
                        {category.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <NavLink to="/search" className={navLinkClass}>
              搜索
            </NavLink>
            <NavLink to="/about" className={navLinkClass}>
              关于我们
            </NavLink>
          </nav>

          <form onSubmit={handleSearch} className="hidden md:flex items-center gap-2 w-64">
            <div className="relative w-full">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-500">
                <i className="ri-search-line text-sm" />
              </span>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影片名称或剧情"
                className="w-full rounded-md border border-background-200/70 bg-background-100 py-2 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-400 transition-colors"
              />
            </div>
            <button
              type="submit"
              className="shrink-0 rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-foreground-950 hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              搜索
            </button>
          </form>

          <div className="flex md:hidden items-center gap-2">
            <Link
              to="/search"
              aria-label="搜索"
              className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors"
            >
              <i className="ri-search-line text-lg" />
            </Link>
            <button
              type="button"
              aria-label="菜单"
              onClick={() => setMobileOpen((prev) => !prev)}
              className="w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors"
            >
              <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} />
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200/60 bg-background-50 px-4 py-4 fade-in">
          <form onSubmit={handleSearch} className="flex items-center gap-2 mb-4">
            <div className="relative w-full">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-500">
                <i className="ri-search-line text-sm" />
              </span>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影片名称或剧情"
                className="w-full rounded-md border border-background-200/70 bg-background-100 py-2 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-400"
              />
            </div>
            <button
              type="submit"
              className="shrink-0 rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-foreground-950 hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              搜索
            </button>
          </form>
          <div className="flex flex-col gap-1">
            {[
              { to: "/", label: "首页" },
              { to: "/search", label: "搜索" },
              { to: "/about", label: "关于我们" },
            ].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setMobileOpen(false)}
                className="px-3 py-2 rounded-md text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors"
              >
                {item.label}
              </Link>
            ))}
          </div>
          <p className="px-3 pt-4 pb-1 text-xs text-foreground-500">影片分类</p>
          <div className="grid grid-cols-2 gap-2">
            {categories.map((category) => (
              <Link
                key={category.id}
                to={`/category/${category.id}`}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-600 hover:text-foreground-950 hover:bg-background-100 transition-colors"
              >
                <span className="w-6 h-6 flex items-center justify-center rounded-md bg-primary-500/15 text-primary-300">
                  <i className={`${category.icon} text-sm`} />
                </span>
                {category.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}